// moduleRole / moduleCan (MODULE-ROLE-CAN-BRIDGES-1) — what an installed app
// may ask about the member looking at it, so it can HIDE what that member
// cannot use: the DFI Finances app hides its cost nav, widgets and views from a
// guest judge, while admins and the "Cost viewers" team (granted the app key
// `dfli.view_costs`) keep them.
//
// The answers come from the workspace read's `access` (server
// WorkspaceController.getWorkspace → callerAccess): the role the read was
// gated on, and the member's effective permission keys in the workspace's org,
// app-declared keys included — the set the records FIELDS axis reads. The
// server's data gates stay the protection; a hidden view would have read
// nothing anyway.
//
//   el.call('moduleRole') → 'owner' | 'admin' | 'editor' | 'viewer' | 'guest'
//                         | null — null while the workspace record (or its
//                           `access`) has not loaded: unknown, never a role.
//   el.call('moduleCan', key) → true | false. Owner and admin always pass;
//                         otherwise the key must be one of the member's
//                         effective permissions. Unknown → false (an app
//                         shows its gated views once the record lands — the
//                         bridges read the live record on every call).
//
// Read-only: neither touches state, so both also answer inside a read-only
// app verb (moduleReadOnlyCall.js refuses only the write-shaped names).
//
// Before the record carries `access` (an older server, or a record from the
// warm-boot snapshot), the org claims still say who is an owner or an admin —
// a workspace's owner/admin role cascades from exactly those org roles — so
// those two answer from the claims and every other member reads as unknown.

import { shellResolveClaims } from './claimResolver.js'

export const MODULE_ROLES = Object.freeze(['owner', 'admin', 'editor', 'viewer', 'guest'])

const _access = (root) => {
  const ws = root && root.workspace
  const access = ws && ws.access
  return access && typeof access === 'object' ? access : null
}

const _claimsRole = (root) => {
  try {
    const claims = (root && shellResolveClaims(root)) || {}
    if (claims.is_owner === true) return 'owner'
    if (claims.org_role === 'admin') return 'admin'
  } catch (_) {
    /* no claims yet — unknown */
  }
  return null
}

export const memberWorkspaceRole = (root) => {
  const access = _access(root)
  if (!access) return _claimsRole(root)
  const role = typeof access.role === 'string' ? access.role.trim().toLowerCase() : ''
  // A platform superuser reads a workspace with the admin's reach.
  if (role === 'superadmin') return 'admin'
  return MODULE_ROLES.includes(role) ? role : null
}

export const memberCan = (root, key) => {
  if (typeof key !== 'string' || !key.trim()) return false
  const role = memberWorkspaceRole(root)
  if (role === 'owner' || role === 'admin') return true
  const access = _access(root)
  return !!(access && Array.isArray(access.permissions) && access.permissions.includes(key.trim()))
}

export const buildModuleRoleBridge = (root) =>
  function moduleRole() {
    return memberWorkspaceRole(root)
  }

export const buildModuleCanBridge = (root) =>
  function moduleCan(key) {
    return memberCan(root, key)
  }
